import React, { useState, useEffect } from 'react';

/**
 * Shimmer effect component for image generation with response timing
 * Shows a placeholder with shimmer animation and timing display in top-right corner
 */
const ImageGenerationShimmer = ({ 
  startTime, 
  className = '', 
  width = 512, 
  height = 512 
}) => {
  const [elapsedTime, setElapsedTime] = useState(0);

  // Update elapsed time every 100ms for smooth timing display
  useEffect(() => {
    if (!startTime) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const elapsed = Math.floor((now - startTime) / 100) / 10; // Convert to seconds with 1 decimal
      setElapsedTime(elapsed);
    }, 100);

    return () => clearInterval(interval);
  }, [startTime]);

  // Format time display
  const formatTime = (seconds) => {
    if (seconds < 60) {
      return `${seconds.toFixed(1)}s`;
    } else {
      const mins = Math.floor(seconds / 60);
      const secs = (seconds % 60).toFixed(1);
      return `${mins}m ${secs}s`;
    }
  };

  return (
    <div className={`relative ${className}`}>
      {/* Shimmer container with fixed dimensions */}
      <div 
        className="relative overflow-hidden rounded-lg border border-bd-50 bg-bg-50"
        style={{ 
          width: `${width}px`, 
          height: `${height}px`,
          maxWidth: '100%',
          aspectRatio: `${width}/${height}`
        }}
      >
        {/* Base shimmer background */}
        <div className="absolute inset-0 bg-gradient-to-r from-bg-50 via-bg-40 to-bg-50 animate-shimmer">
          {/* Shimmer overlay animation */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer-wave"></div>
        </div>

        {/* Response timing display in top-right corner */}
        {startTime && (
          <div className="absolute top-3 right-3 z-10">
            <div className="bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-md font-mono">
              {formatTime(elapsedTime)}
            </div>
          </div>
        )}

        {/* Optional: Add a subtle icon or text in center */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-fg-60 text-sm font-medium opacity-50">
            Generating image...
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageGenerationShimmer;